export function sanitizeResponsibleBack(raw) {
  if (!raw) return "";
  let candidate = String(raw).trim();
  if (!candidate.startsWith("/")) {
    try {
      candidate = decodeURIComponent(candidate).trim();
    } catch {
      return "";
    }
  }
  if (!candidate.startsWith("/") || candidate.startsWith("//") || candidate.includes("..")) return "";
  try {
    const u = new URL(candidate, window.location.origin);
    if (u.origin !== window.location.origin) return "";
    if (u.pathname === "/portail-medecin" || u.pathname === "/portail-medecin/") {
      return `${u.pathname}${u.search}${u.hash}`;
    }
    if (!/^\/(multisecteur\/)?(responsable|superviseur)(\/|$)/.test(u.pathname)) return "";
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return "";
  }
}

export function withResponsibleBack(path, rawBack) {
  const safeBack = sanitizeResponsibleBack(rawBack);
  if (!safeBack) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}back=${encodeURIComponent(safeBack)}`;
}

function sameResponsibleNavigationLevel(a, b) {
  if (a.pathname !== b.pathname) return false;
  const keys = ["type", "secteur", "lockSecteur", "popup"];
  return keys.every((key) => (a.searchParams.get(key) || "") === (b.searchParams.get(key) || ""));
}

export function collapseResponsibleSelfBack(rawBack) {
  const safeBack = sanitizeResponsibleBack(rawBack);
  if (!safeBack || typeof window === "undefined") return safeBack;

  try {
    const url = new URL(safeBack, window.location.origin);
    for (let depth = 0; depth < 8; depth += 1) {
      const nestedBack = sanitizeResponsibleBack(url.searchParams.get("back") || "");
      if (!nestedBack) break;
      const nestedUrl = new URL(nestedBack, window.location.origin);
      if (!sameResponsibleNavigationLevel(url, nestedUrl)) break;
      const nextBack = sanitizeResponsibleBack(nestedUrl.searchParams.get("back") || "");
      if (nextBack) {
        url.searchParams.set("back", nextBack);
      } else {
        url.searchParams.delete("back");
      }
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return safeBack;
  }
}

export function withResponsibleBridgeContext(path, { backMode = "current" } = {}) {
  if (typeof window === "undefined") return path;

  const currentUrl = new URL(window.location.href);
  const currentSearchParams = currentUrl.searchParams;
  const currentIsLocked = currentSearchParams.get("lockSecteur") === "1";
  const target = new URL(path, window.location.origin);

  if (currentIsLocked) {
    target.searchParams.set("lockSecteur", "1");
  }

  if (!target.searchParams.has("back")) {
    const currentRelative = collapseResponsibleSelfBack(`${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    const inheritedBack = sanitizeResponsibleBack(currentSearchParams.get("back") || "");
    if (backMode === "inherited" && inheritedBack) {
      const inheritedUrl = new URL(inheritedBack, window.location.origin);
      if (target.pathname === inheritedUrl.pathname) {
        return `${inheritedUrl.pathname}${inheritedUrl.search}${inheritedUrl.hash}`;
      }
    }
    const backValue = backMode === "inherited" ? inheritedBack : currentRelative;
    if (backValue) {
      target.searchParams.set("back", backValue);
    }
  }

  return `${target.pathname}${target.search}${target.hash}`;
}

export function hasSupervisorResponsibleBackChain(rawBack, depth = 0) {
  if (typeof window === "undefined" || depth > 8) return false;
  const safeBack = sanitizeResponsibleBack(rawBack);
  if (!safeBack) return false;

  try {
    const backUrl = new URL(safeBack, window.location.origin);
    if (/^\/(multisecteur\/)?superviseur(\/|$)/.test(backUrl.pathname)) {
      return true;
    }
    if (!/^\/(multisecteur\/)?responsable(\/|$)/.test(backUrl.pathname)) {
      return false;
    }
    return hasSupervisorResponsibleBackChain(backUrl.searchParams.get("back") || "", depth + 1);
  } catch {
    return false;
  }
}

export function buildGardesHubBackPath(typeGarde, rawBack, { lockSecteur = false } = {}) {
  let hubPath = `/responsable/gardes?type=${encodeURIComponent(typeGarde)}`;
  if (lockSecteur) hubPath += "&lockSecteur=1";

  const safeBack = sanitizeResponsibleBack(rawBack);
  if (!safeBack) return hubPath;

  if (safeBack === "/responsable/gardes" || safeBack.startsWith("/responsable/gardes?")) {
    return safeBack;
  }
  return withResponsibleBack(hubPath, safeBack);
}

export function buildResponsibleHubBackPath(hubPath, rawBack) {
  const safeBack = sanitizeResponsibleBack(rawBack);
  if (!safeBack) return hubPath;
  if (safeBack === hubPath || safeBack.startsWith(`${hubPath}?`)) return safeBack;
  return withResponsibleBack(hubPath, safeBack);
}
